import React, { useState } from 'react';
import { XMarkIcon, DocumentArrowDownIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { templateService } from '../services/api';
import { toast } from 'react-hot-toast';
import { useLanguage } from '../contexts/LanguageContext';

const MetaTemplateBroadcastDialog = ({ isOpen, onClose, template }) => {
  const { t } = useLanguage();
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [loading, setLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      setFile(selectedFile);
      setFileName(selectedFile.name);
      previewCSV(selectedFile);
    }
  };

  const previewCSV = async (file) => {
    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      if (lines.length === 0) {
        setPreviewData(null);
        return;
      }

      // Parse CSV (simple parsing, assumes comma-separated)
      const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
      const rows = lines.slice(1, Math.min(6, lines.length)).map(line => {
        const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
        const row = {};
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });
        return row;
      });

      setPreviewData({ headers, rows, totalRows: lines.length - 1 });
    } catch (error) {
      console.error('Error previewing CSV:', error);
      toast.error('Failed to preview CSV file');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await templateService.downloadTemplateCSV(template.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `template_${template.name}_broadcast.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('CSV template downloaded successfully');
    } catch (error) {
      console.error('Error downloading CSV template:', error);
      toast.error('Failed to download CSV template');
    }
  };

  const handleSubmit = async () => {
    if (!file) {
      toast.error('Please select a CSV file');
      return;
    }

    setUploading(true);
    try {
      const response = await templateService.uploadBroadcastCSV(template.id, file);

      if (response.success) {
        const { successful, failed, total_rows } = response.data;
        toast.success(
          `Broadcast completed! ${successful} sent, ${failed} failed out of ${total_rows} total rows.`,
          { duration: 5000 }
        );
        onClose();
      } else {
        toast.error(response.message || 'Failed to send broadcast');
      }
    } catch (error) {
      console.error('Error sending broadcast:', error);
      toast.error(error.response?.data?.detail || 'Failed to send broadcast');
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen || !template) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {t('metaTemplates.sendBroadcast', 'Send Broadcast')}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Template: {template.name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Template Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-blue-900 mb-2">Template Information</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-blue-700">Name:</span>
                  <span className="ml-2 text-blue-900">{template.name}</span>
                </div>
                <div>
                  <span className="text-blue-700">Category:</span>
                  <span className="ml-2 text-blue-900">{template.category}</span>
                </div>
                <div>
                  <span className="text-blue-700">Language:</span>
                  <span className="ml-2 text-blue-900">{template.language}</span>
                </div>
                <div>
                  <span className="text-blue-700">Variables:</span>
                  <span className="ml-2 text-blue-900">
                    {template.variables?.length || 0}
                  </span>
                </div>
              </div>
            </div>

            {/* Download Template */}
            <div>
              <button
                onClick={handleDownloadTemplate}
                className="flex items-center px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100"
              >
                <DocumentArrowDownIcon className="w-5 h-5 mr-2" />
                {t('metaTemplates.downloadCSVTemplate', 'Download CSV Template')}
              </button>
              <p className="mt-2 text-sm text-gray-500">
                Download a CSV template with the correct format. Fill in the data and upload it to send broadcast messages.
              </p>
            </div>

            {/* File Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('metaTemplates.uploadCSV', 'Upload CSV File')} *
              </label>
              <div className="flex items-center gap-4">
                <label className="flex-1 cursor-pointer">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <div className="flex items-center justify-center px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50">
                    <span className="text-sm text-gray-600">
                      {fileName || t('metaTemplates.selectCSVFile', 'Click to select CSV file')}
                    </span>
                  </div>
                </label>
              </div>
            </div>

            {/* CSV Preview */}
            {previewData && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">
                  CSV Preview (First {previewData.rows.length} rows of {previewData.totalRows} total)
                </h3>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          {previewData.headers.map((header, index) => (
                            <th
                              key={index}
                              className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase"
                            >
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {previewData.rows.map((row, rowIndex) => (
                          <tr key={rowIndex}>
                            {previewData.headers.map((header, colIndex) => (
                              <td
                                key={colIndex}
                                className="px-4 py-2 text-sm text-gray-900"
                              >
                                {row[header] || ''}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Instructions */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-yellow-900 mb-2">Instructions:</h4>
              <ul className="text-sm text-yellow-800 space-y-1 list-disc list-inside">
                <li>Download the CSV template to see the required format</li>
                <li>Fill in the template_name, district_code, phone_number, and variable columns</li>
                <li>Upload the completed CSV file to send broadcast messages</li>
                <li>Only approved templates can be used for broadcasting</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={!file || uploading}
            className="flex items-center px-4 py-2 text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            {uploading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {t('common.sending', 'Sending...')}
              </>
            ) : (
              <>
                <PaperAirplaneIcon className="w-5 h-5 mr-2" />
                {t('metaTemplates.sendBroadcast', 'Send Broadcast')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MetaTemplateBroadcastDialog;

